// Light or dark: the system decides unless the resident says otherwise.
// The choice is remembered in this browser, like "your lines".
import { token } from './tokens.ts';
import type { ThemeName } from './tokens.ts';

export type ThemePref = 'system' | 'light' | 'dark';

const KEY = 'poznan-teraz:theme';
const PREFS = ['system', 'light', 'dark'] as const satisfies readonly ThemePref[];
const dark = matchMedia('(prefers-color-scheme: dark)');
const listeners = new Set<(resolved: ThemeName) => void>();

function load(): ThemePref {
  try {
    const saved = localStorage.getItem(KEY);
    // read the stored string back against the known preferences rather than trusting it
    return PREFS.find((p) => p === saved) ?? 'system';
  } catch {
    return 'system';
  }
}

let pref: ThemePref = load();

const resolve = (): ThemeName => (pref === 'system' ? (dark.matches ? 'dark' : 'light') : pref);

/** The tokens follow `[data-theme]`; "system" means no attribute at all. */
function apply() {
  const root = document.documentElement;
  if (pref === 'system') delete root.dataset.theme;
  else root.dataset.theme = pref;
  // keeps native widgets and scrollbars on the same side as the tokens
  root.style.colorScheme = pref === 'system' ? '' : pref;
  paintBrowserChrome();
}

/**
 * The browser's own bar takes the paper colour (absorbs #45). The two metas in
 * index.html carry one scheme each for the first paint; a forced theme puts the
 * same colour in both, so whichever one the OS matches gives the forced answer.
 */
function paintBrowserChrome() {
  const metas = document.querySelectorAll<HTMLMetaElement>('meta[name="theme-color"]');
  for (const meta of metas) {
    const scheme: ThemeName = meta.media.includes('dark') ? 'dark' : 'light';
    meta.content = token('bg', pref === 'system' ? scheme : pref);
  }
}

function announce() {
  const resolved = resolve();
  for (const fn of listeners) fn(resolved);
}

// the OS switching over at dusk only matters while the resident is on "system"
dark.addEventListener('change', () => {
  if (pref === 'system') announce();
});

apply();

export const theme = {
  get: () => pref,
  resolved: resolve,
  set(next: ThemePref) {
    if (pref === next) return;
    pref = next;
    try {
      localStorage.setItem(KEY, next);
    } catch {
      /* private mode etc. */
    }
    apply();
    announce();
  },
  /** Called with the resolved theme now and whenever it changes. */
  subscribe(fn: (resolved: ThemeName) => void) {
    listeners.add(fn);
    fn(resolve());
    return () => listeners.delete(fn);
  },
};
